import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';

const BASE = '/admin/newsletter';

// API helpers
const api = {
    list: (params) => axiosInstance.get(BASE, { params }).then(r => r.data),
    get: (id) => axiosInstance.get(`${BASE}/${id}`).then(r => r.data),
    create: (payload) => axiosInstance.post(BASE, payload).then(r => r.data),
    update: (id, payload) => axiosInstance.put(`${BASE}/${id}`, payload).then(r => r.data),
    remove: (id) => axiosInstance.delete(`${BASE}/${id}`).then(r => r.data),
    exportCsv: (params) => axiosInstance.get(`${BASE}/export`, { params, responseType: 'blob' }).then(r => r.data),
};

// Thunks
export const fetchSubscribers = createAsyncThunk(
    'newsletter/fetchSubscribers',
    async ({ page = 1, limit = 20, search = '', status = '' } = {}, thunkAPI) => {
        try {
            const res = await api.list({ page, limit, search, status });
            return res;
        } catch (e) {
            return thunkAPI.rejectWithValue(e?.response?.data?.message || e.message);
        }
    }
);

export const getSubscriber = createAsyncThunk(
    'newsletter/getSubscriber',
    async (id, thunkAPI) => {
        try {
            const res = await api.get(id);
            return res.item;
        } catch (e) {
            return thunkAPI.rejectWithValue(e?.response?.data?.message || e.message);
        }
    }
);

export const createSubscriber = createAsyncThunk(
    'newsletter/createSubscriber',
    async ({ email, unsubscribed = false }, thunkAPI) => {
        try {
            const res = await api.create({ email, unsubscribed });
            return res.item;
        } catch (e) {
            return thunkAPI.rejectWithValue(e?.response?.data?.message || e.message);
        }
    }
);

export const updateSubscriber = createAsyncThunk(
    'newsletter/updateSubscriber',
    async ({ id, payload }, thunkAPI) => {
        try {
            const res = await api.update(id, payload);
            return res.item;
        } catch (e) {
            return thunkAPI.rejectWithValue(e?.response?.data?.message || e.message);
        }
    }
);

export const deleteSubscriber = createAsyncThunk(
    'newsletter/deleteSubscriber',
    async (id, thunkAPI) => {
        try {
            await api.remove(id);
            return id;
        } catch (e) {
            return thunkAPI.rejectWithValue(e?.response?.data?.message || e.message);
        }
    }
);

export const exportSubscribers = createAsyncThunk(
    'newsletter/exportSubscribers',
    async ({ search = '', status = '' } = {}, thunkAPI) => {
        try {
            const blob = await api.exportCsv({ search, status });
            return blob;
        } catch (e) {
            return thunkAPI.rejectWithValue(e?.response?.data?.message || e.message);
        }
    }
);

// Slice
const initialState = {
    items: [],
    selected: null,
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
    search: '',
    statusFilter: '',
    loading: false,
    error: null,
    exporting: false,
};

const newsletterSlice = createSlice({
    name: 'newsletter',
    initialState,
    reducers: {
        setPage(state, action) { state.page = action.payload || 1; },
        setLimit(state, action) { state.limit = action.payload || 20; },
        setSearch(state, action) { state.search = action.payload || ''; state.page = 1; },
        setStatusFilter(state, action) { state.statusFilter = action.payload || ''; state.page = 1; },
        clearError(state) { state.error = null; },
        clearSelected(state) { state.selected = null; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSubscribers.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchSubscribers.fulfilled, (state, action) => {
                state.loading = false;
                state.items = action.payload.items || [];
                state.page = action.payload.page || 1;
                state.limit = action.payload.limit || state.limit;
                state.total = action.payload.total || 0;
                state.pages = action.payload.pages || Math.ceil((action.payload.total || 0) / (action.payload.limit || state.limit)) || 1;
            })
            .addCase(fetchSubscribers.rejected, (state, action) => { state.loading = false; state.error = action.payload || 'Failed to load subscribers'; })

            .addCase(getSubscriber.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(getSubscriber.fulfilled, (state, action) => { state.loading = false; state.selected = action.payload; })
            .addCase(getSubscriber.rejected, (state, action) => { state.loading = false; state.error = action.payload || 'Failed to load subscriber'; })

            .addCase(createSubscriber.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(createSubscriber.fulfilled, (state, action) => { state.loading = false; state.items.unshift(action.payload); state.total += 1; })
            .addCase(createSubscriber.rejected, (state, action) => { state.loading = false; state.error = action.payload || 'Failed to create subscriber'; })

            .addCase(updateSubscriber.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(updateSubscriber.fulfilled, (state, action) => {
                state.loading = false;
                const idx = state.items.findIndex(i => i._id === action.payload._id);
                if (idx >= 0) state.items[idx] = action.payload;
                if (state.selected?._id === action.payload._id) state.selected = action.payload;
            })
            .addCase(updateSubscriber.rejected, (state, action) => { state.loading = false; state.error = action.payload || 'Failed to update subscriber'; })

            .addCase(deleteSubscriber.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(deleteSubscriber.fulfilled, (state, action) => {
                state.loading = false;
                state.items = state.items.filter(i => i._id !== action.payload);
                state.total = Math.max(0, state.total - 1);
            })
            .addCase(deleteSubscriber.rejected, (state, action) => { state.loading = false; state.error = action.payload || 'Failed to delete subscriber'; })

            .addCase(exportSubscribers.pending, (state) => { state.exporting = true; state.error = null; })
            .addCase(exportSubscribers.fulfilled, (state) => { state.exporting = false; })
            .addCase(exportSubscribers.rejected, (state, action) => { state.exporting = false; state.error = action.payload || 'Failed to export CSV'; });
    },
});

export const {
    setPage,
    setLimit,
    setSearch,
    setStatusFilter,
    clearError,
    clearSelected,
} = newsletterSlice.actions;

export default newsletterSlice.reducer;

// Selectors
export const selectNewsletter = (state) => state.newsletter;
export const selectSubscribers = (state) => state.newsletter.items;